import React from 'react';
import { Link } from 'react-router-dom';

const GRID_BG = {
  backgroundImage:
    'linear-gradient(rgba(201,168,76,0.12) 1px, transparent 1px), linear-gradient(90deg, rgba(201,168,76,0.12) 1px, transparent 1px)',
  backgroundSize: '60px 60px',
};

const DifferentiatorCard = ({ title, desc }) => (
  <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:border-gold-200 hover:shadow-md transition-all">
    <div className="w-10 h-10 bg-gold-500/10 border border-gold-200 rounded-xl flex items-center justify-center mb-5">
      <div className="w-3 h-3 bg-gold-500 rounded-full" />
    </div>
    <h3 className="text-lg font-bold text-navy-800 font-heading mb-3">{title}</h3>
    <p className="text-slate-600 text-sm leading-relaxed">{desc}</p>
  </div>
);

const DIFFERENTIATORS = [
  {
    title: 'Elite Professionals',
    desc: 'Our consultants hold advanced Engineering degrees and MBAs from accredited universities alongside active PMP® credentials — delivering a rare blend of corporate financial literacy, strategic thinking, and global execution standards.',
  },
  {
    title: 'Turnaround & Project Rescue Specialists',
    desc: 'We don\'t just manage steady-state portfolios. DTX excels in high-pressure environments, stepping into complex, failing initiatives, diagnosing systemic friction, and rapidly turning them toward measurable success.',
  },
  {
    title: 'Adaptive & Hybrid Framework Delivery',
    desc: 'Aligned with modern PMBOK principles and PRINCE2 7 standards, we tailor hybrid delivery models — blending predictive baseline governance with adaptive, iterative frameworks — to fit your initiative\'s speed, culture, and risk landscape.',
  },
  {
    title: 'Independent Validation & Verification (IV&V)',
    desc: 'We act as an objective, third-party validator for complex programs executed by other major contractors, safeguarding your investment against blind spots, performance variance, and delivery drift.',
  },
];

const FRAMEWORKS = [
  'PMBOK', 'PRINCE2 7', 'Agile / Scrum', 'Kanban', 'Waterfall',
  'IV&V', 'EVM', 'Risk Management', 'Stakeholder Engagement', 'PMO Standup',
];

const ProgramManagement = () => {
  return (
    <div className="pt-20">

      {/* Page header */}
      <section className="relative bg-navy-800 py-20 overflow-hidden">
        <div className="absolute inset-0" style={GRID_BG} />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gold-500/5 rounded-full blur-3xl -translate-y-1/3 translate-x-1/3" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm text-slate-400 mb-6">
            <Link to="/" className="hover:text-gold-400 transition-colors">Home</Link>
            <span>/</span>
            <span className="text-gold-400">Program & Project Management</span>
          </nav>
          <p className="text-gold-500 text-sm font-bold uppercase tracking-widest mb-4">Service Line</p>
          <h1 className="text-4xl md:text-5xl font-black text-white font-heading leading-tight max-w-3xl">
            Program &amp; Project Management
          </h1>
          <p className="text-slate-300 text-lg mt-6 max-w-2xl leading-relaxed">
            Powered by MBA graduates and PMP®-certified professionals, DTX bridges the gap between executive
            vision and frontline reality — converting complex organizational investments into predictable,
            measurable value.
          </p>
        </div>
      </section>

      {/* Intro section */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-gold-600 text-sm font-bold uppercase tracking-widest mb-4">Strategic PMO</p>
              <h2 className="text-3xl font-black text-navy-800 font-heading mb-6">
                Systemic Governance &amp; Project Rescue
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                DTX professionals bring multi-year, tier-one experience standing up Project Management Offices
                (PMOs) within very large, complex organizations. Modern project delivery has evolved beyond task
                coordination — success requires navigating highly interconnected ecosystems, managing
                multifaceted stakeholder environments, and pivoting to an outcome-driven value delivery model.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Our resources have spent years counseling Fortune 10 executives, helping corporations protect
                their margins, manage risk, and execute massive digital and operational transformations. We bring
                that same rigor to federal agencies and mission-driven non-profits — where strict budget
                stewardship and consensus-building across diverse stakeholders are paramount.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-6">
              {[
                { stat: '30+', label: 'Years Combined Experience' },
                { stat: 'F10', label: 'Fortune 10 Engagements' },
                { stat: 'PMP®', label: 'Certified Project Managers' },
                { stat: 'IV&V', label: 'Independent Validation' },
              ].map(({ stat, label }) => (
                <div key={label} className="bg-slate-50 rounded-2xl p-6 border border-slate-100 text-center">
                  <p className="text-3xl font-black text-gold-500 font-heading mb-1">{stat}</p>
                  <p className="text-slate-600 text-sm font-medium">{label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Differentiators */}
      <section className="bg-slate-50 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-gold-600 text-sm font-bold uppercase tracking-widest mb-4">Why DTX</p>
            <h2 className="text-4xl font-black text-navy-800 font-heading">Why Partner with DTX</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {DIFFERENTIATORS.map((d) => (
              <DifferentiatorCard key={d.title} {...d} />
            ))}
          </div>
        </div>
      </section>

      {/* Frameworks */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-gold-600 text-sm font-bold uppercase tracking-widest mb-4">Methodologies & Frameworks</p>
            <h2 className="text-3xl font-black text-navy-800 font-heading">How We Deliver</h2>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {FRAMEWORKS.map((f) => (
              <span
                key={f}
                className="bg-slate-50 border border-slate-200 text-navy-700 px-4 py-2 rounded-full text-sm font-semibold hover:border-gold-300 hover:bg-gold-50 transition-colors"
              >
                {f}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy-800 py-16 relative overflow-hidden">
        <div className="absolute inset-0" style={GRID_BG} />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-black text-white font-heading mb-4">
            Need a PMO or Project Rescue?
          </h2>
          <p className="text-slate-300 mb-8 max-w-lg mx-auto">
            Whether standing up a new PMO or rescuing a stalled initiative, DTX brings the expertise to
            deliver measurable results on time and within budget.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-gold-500 hover:bg-gold-400 text-navy-900 px-8 py-4 rounded-xl font-bold transition-colors shadow-lg"
          >
            Get in Touch
          </Link>
        </div>
      </section>

    </div>
  );
};

export default ProgramManagement;
